import {
  Component,
  DestroyRef,
  signal,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { ClientInfoComponent } from '../client-info/client-info.component';
import {
  ClientSubjectData,
  UiSession,
  ClientSessionService
} from '@brighthub/uisession';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { MessageService } from 'primeng/api';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { IftaLabel } from 'primeng/iftalabel';
import { ClientSearchService } from '../client-search.service';
import { AmplitudeService } from '@brighthub/logging';

type SearchResult =
  | { type: 'session'; value: UiSession }
  | { type: 'subject'; value: ClientSubjectData };

const mockUiSessionFromSubject = (subject: ClientSubjectData): UiSession => ({
  uiSessionId: 'temp-' + subject.guid,
  oktaUserId: '', // can leave blank
  subjectId: subject.guid,
  subjectType: 'client',
  conversationId: undefined,
  createdDate: '',
  subjectData: subject,
  state: 'active',
});


@Component({
  selector: 'bai-client-search',
  imports: [
    CommonModule,
    FormsModule,
    AutoCompleteModule,
    ClientInfoComponent,
    IftaLabel,
  ],
  styleUrls: ['./client-search.component.scss'],
  templateUrl: './client-search.component.html',
})
export class ClientSearchComponent {
  @Output() clientSelected = new EventEmitter<UiSession>();

  private readonly _clientService = inject(ClientSessionService);
  private readonly _api = inject(ApiService);
  private readonly _messageService = inject(MessageService);
  private readonly _clientSearchService = inject(ClientSearchService);
  private readonly _destroyRef = inject(DestroyRef);
  private _amplitude = inject(AmplitudeService);

  private _results = signal<SearchResult[]>([]);
  results = this._results.asReadonly();

  private _loadingFromApi = signal(false);
  loadingFromApi = this._loadingFromApi.asReadonly();
  query: any;
  emptyMessage = '';
  private readonly _remoteSearch$ = new Subject<string>();

  constructor() {
    this._remoteSearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((query) => this._remoteSearch(query));
  }

  searchClients(event: { query: string }) {
    this.emptyMessage = '';
    const q = event.query.trim().toLowerCase() || '';

    // Immediate local match
    const localMatches = this._clientService
      .sessions()
      .filter((s) => s.subjectData.guid.toLowerCase().includes(q));

    if (localMatches.length > 0) {
      this._results.set(
        localMatches.map((s) => ({ type: 'session', value: s }))
      );
      this._loadingFromApi.set(false);
    } else {
      this._results.set([]);
      this._loadingFromApi.set(false);

      // Only trigger remote search if query is long enough
      if (q.length >= 9) {
        this._remoteSearch$.next(q);
      }
    }
  }

  private _remoteSearch(q: string) {
    this._loadingFromApi.set(true);

    this._clientSearchService.searchByGuid(q).subscribe({
      next: (res) => {
        if (
          res.result.code === GenericResponseCode.SERVICE_OK &&
          res.payload?.guid
        ) {
          const mockSession: UiSession = mockUiSessionFromSubject(res.payload);

          this._results.set([{ type: 'session', value: mockSession }]);
        } else {
          this._results.set([]);
        }

        this._amplitude.logEvent('client_search_remote_success', { query: q, results: this._results().length });
        this._loadingFromApi.set(false);
      },
      error: (err) => {
        if (err.status !== 404) {
          this.emptyMessage = err.message;
        }

        this._amplitude.logEvent('client_search_remote_error', { query: q, message: err.message, status: err.status });
        this._results.set([]);
        this._loadingFromApi.set(false);
      },
    });
  }


  onClientSelect(event: AutoCompleteSelectEvent) {
    this.query = null;
    this._results.set([]);
    this._loadingFromApi.set(false);
  }

}
